import { all } from 'redux-saga/effects'
import { ISagaModule } from 'redux-dynamic-modules-saga'

import { EventPointOfInterest, GenericPointOfInterest, StopPointOfInterest, Stop, Tour, TourCategory, CarouselItem, Mood, VisitorType } from 'generated/mos/org/getty/audio'
import {
  GetStopRequest,
  ListStopsRequest,
  ListStopsResponse,
  GetTourRequest,
  GetTourResponse,
  ListToursRequest,
  ListToursResponse,
  GetMoodRequest,
  GetMoodResponse,
  ListMoodsRequest,
  ListMoodsResponse,
  ListTourCategoriesResponse,
  GetCarouselItemRequest,
  GetCarouselItemResponse,
  ListCarouselItemsRequest,
  GetStopPointOfInterestRequest,
  GetEventPointOfInterestRequest,
  GetGenericPointOfInterestRequest,
  GetVisitorTypeRequest,
  GetVisitorTypeResponse,
  ListVisitorTypesRequest,
  ListVisitorTypesResponse,
} from 'generated/mos/org/getty/audioaccess'
import {
  CreateStopRequest,
  CreateStopResponse,
  UpdateStopRequest,
  CreateTourRequest,
  CreateTourResponse,
  DeleteTourRequest,
  DeleteTourResponse,
  UpdateTourRequest,
  UpdateTourResponse,
  CreateMoodRequest,
  CreateMoodResponse,
  DeleteMoodRequest,
  DeleteMoodResponse,
  UpdateMoodRequest,
  UpdateMoodResponse,
  DeleteStopRequest,
  DeleteStopResponse,
  CreateTourCategoryRequest,
  CreateTourCategoryResponse,
  DeleteTourCategoryRequest,
  DeleteTourCategoryResponse,
  UpdateTourCategoryRequest,
  UpdateTourCategoryResponse,
  UpdateTourCategoriesRequest,
  UpdateTourCategoriesResponse,
  CreateCarouselItemRequest,
  CreateCarouselItemResponse,
  UpdateCarouselItemRequest,
  UpdateCarouselItemResponse,
  CreateStopPointOfInterestRequest,
  CreateStopPointOfInterestResponse,
  UpdateStopPointOfInterestRequest,
  UpdateStopPointOfInterestResponse,
  DeleteStopPointOfInterestRequest,
  DeleteStopPointOfInterestResponse,
  CreateEventPointOfInterestRequest,
  CreateEventPointOfInterestResponse,
  UpdateEventPointOfInterestRequest,
  UpdateEventPointOfInterestResponse,
  DeleteEventPointOfInterestRequest,
  DeleteEventPointOfInterestResponse,
  CreateGenericPointOfInterestRequest,
  CreateGenericPointOfInterestResponse,
  UpdateGenericPointOfInterestRequest,
  UpdateGenericPointOfInterestResponse,
  DeleteGenericPointOfInterestRequest,
  DeleteGenericPointOfInterestResponse,
  CreateVisitorTypeRequest,
  CreateVisitorTypeResponse,
  UpdateVisitorTypeRequest,
  DeleteVisitorTypeRequest,
  DeleteVisitorTypeResponse,
} from 'generated/mos/org/getty/audiomanagement'
import {
  ListBundleSummariesRequest,
  ListBundleSummariesResponse,
  ListReleasesRequest,
  ListReleasesResponse,
  ListBundlesRequest,
  ListBundlesResponse,
} from 'generated/mos/org/getty/publishaccess'
import {
  CreateReleaseRequest,
  CreateReleaseResponse,
} from 'generated/mos/org/getty/publishmanagement'
import { VisitorInfoPage, VisitorInfoSiteContent, Amenity } from 'generated/mos/org/getty/visitorinfo'
import {
  ListValidationFailuresRequest,
  ListValidationFailuresResponse,
} from 'generated/mos/org/getty/validationaccess'
import {
  GetAmenityRequest,
  GetAmenityResponse,
  GetVisitorInfoPageRequest,
  GetVisitorInfoPageResponse,
  ListVisitorInfoPagesResponse,
} from 'generated/mos/org/getty/visitorinfoaccess'
import {
  CreateVisitorInfoPageRequest,
  CreateVisitorInfoPageResponse,
  DeleteVisitorInfoPageRequest,
  DeleteVisitorInfoPageResponse,
  DeleteAmenityRequest,
  DeleteAmenityResponse,
  CreateAmenityRequest,
  CreateAmenityResponse,
  UpdateVisitorInfoPageRequest,
  UpdateVisitorInfoPageResponse,
  UpdateAmenityRequest,
  UpdateAmenityResponse,
  UpdateVisitorInfoSiteContentResponse,
} from 'generated/mos/org/getty/visitorinfomanagement'
import {
  ListTMSObjectsRequest,
  ListTMSObjectsResponse,
  TMSObject,
} from 'generated/mos/org/getty/integrationtms'
import { GetLinkedFieldMappingsResponse } from 'generated/mos/linkedfieldmappings'
import { ResolvedValueResult } from 'generated/mos/linkedfieldresolver'
import { Site } from 'generated/mos/structure'
import { ListSitesResponse } from 'generated/mos/structureaccess'

import { assertNever, Invariant } from 'helpers/core'
import { ErrorMessage } from 'helpers/sagas'
import { PaginatedList, Status } from 'helpers/status'

import { gettyRootSaga } from './sagas'
import {
  AmentityUpdate,
  AnalyticsResponse,
  GettyAppState,
  initialGettyAppState,
  StopWithSpace,
  VisitorInfoPages,
  ListAllValidationFailures,
} from './store'
import { Media } from 'generated/mos/media'
import {
  ListGetty360EventsRequest,
  ListGetty360EventsResponse
} from "../../../generated/mos/org/getty/integration";

type GettyListStopsSuccess = ListStopsResponse.Entity & {
  readonly stops: ReadonlyArray<StopWithSpace>;
  readonly cumulativePaginationEnabled?: boolean;
};

export type GettyGetTourSuccess = {
  readonly type: GetTourResponse.Entity['type'];
  readonly tour: Tour.Entity & {
    readonly stops?: ReadonlyArray<Stop.Entity>;
  };
}

export type GettyUpdateTourSuccess = {
  readonly type: UpdateTourResponse.Entity['type'];
  readonly tour: Tour.Entity & {
    readonly stops?: ReadonlyArray<Stop.Entity>;
  };
}

export type GettyGetMoodSuccess = {
  readonly type: GetMoodResponse.Entity['type'];
  readonly mood: Mood.Entity & {
    readonly stopPointsOfInterest?: ReadonlyArray<StopPointOfInterest.Entity>;
    readonly genericPointsOfInterest?: ReadonlyArray<GenericPointOfInterest.Entity>;
    readonly eventPointsOfInterest?: ReadonlyArray<EventPointOfInterest.Entity>;
  };
}

export type GettyUpdateMoodSuccess = {
  readonly type: UpdateMoodResponse.Entity['type'];
  readonly mood: Mood.Entity & {
    readonly stopPointsOfInterest?: ReadonlyArray<StopPointOfInterest.Entity>;
    readonly genericPointsOfInterest?: ReadonlyArray<GenericPointOfInterest.Entity>;
    readonly eventPointsOfInterest?: ReadonlyArray<EventPointOfInterest.Entity>;
  };
}

export type GettyVisitorInfoSuccess = {
  readonly type: ListVisitorInfoPagesResponse.Entity['type'];
  readonly siteContentRef: VisitorInfoSiteContent.Ref;
  readonly pages: ReadonlyArray<{
    readonly page: VisitorInfoPage.Entity;
    readonly amenities: ReadonlyArray<Amenity.Entity> | undefined;
  }>;
}

export type GettyUpdateStopPointOfInterestSuccess = {
  readonly type: UpdateStopPointOfInterestResponse.Entity['type'];
  readonly stopPointOfInterest: StopPointOfInterest.Entity;
}

export type GettyUpdateEventPointOfInterestSuccess = {
  readonly type: UpdateEventPointOfInterestResponse.Entity['type'];
  readonly eventPointOfInterest: EventPointOfInterest.Entity;
}

export const actionCreators = {
  // {{{ audio access.
  gettyGetStopRequest: (payload: GetStopRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopRequest' as const,
    payload,
  }),
  gettyGetStopSuccess: (
    stop: Stop.Entity | undefined,
    tours: ReadonlyArray<Tour.Entity> | undefined,
    relatedStops?: ReadonlyArray<Stop.Entity>,
    baseEntity?: TMSObject.Entity,
    fieldMappings?: ReadonlyArray<GetLinkedFieldMappingsResponse.Mapping.Entity>,
    resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>,
    moods?: ReadonlyArray<Mood.Entity>,
  ) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopSuccess' as const,
    baseEntity,
    fieldMappings,
    resolvedValues,
    relatedStops,
    stop,
    tours,
    moods,
  }),
  gettyGetStopFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopFailure' as const,
    message,
  }),
  gettyListStopsRequest: (payload: ListStopsRequest.Entity, cumulativePaginationEnabled?: boolean) => ({
    domain: 'getty' as const,
    type: 'gettyListStopsRequest' as const,
    payload,
    cumulativePaginationEnabled,
  }),
  gettyListStopsSearchRequest: (payload: ListStopsRequest.Entity, cumulativePaginationEnabled?: boolean) => ({
    domain: 'getty' as const,
    type: 'gettyListStopsSearchRequest' as const,
    payload,
    cumulativePaginationEnabled,
  }),
  gettyListStopsSuccess: (payload: GettyListStopsSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyListStopsSuccess' as const,
   payload,
  }),
  gettyListStopsFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListStopsFailure' as const,
    message,
  }),
  gettyListStopsReset: () => ({
    domain: 'getty' as const,
    type: 'gettyListStopsReset' as const
  }),
  gettyGetTourRequest: (payload: GetTourRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetTourRequest' as const,
    payload,
  }),
  gettyGetTourSuccess: (payload: GettyGetTourSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyGetTourSuccess' as const,
    payload,
  }),
  gettyGetTourFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetTourFailure' as const,
    message,
  }),
  gettyListToursRequest: (payload: ListToursRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListToursRequest' as const,
    payload,
  }),
  gettyListToursSuccess: (payload: ListToursResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListToursSuccess' as const,
    payload,
  }),
  gettyListToursFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListToursFailure' as const,
    message,
  }),

  gettyGetMoodRequest: (payload: GetMoodRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetMoodRequest' as const,
    payload,
  }),
  gettyGetMoodSuccess: (payload: GettyGetMoodSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyGetMoodSuccess' as const,
    payload,
  }),
  gettyGetMoodFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetMoodFailure' as const,
    message,
  }),
  gettyListMoodsRequest: (payload: ListMoodsRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListMoodsRequest' as const,
    payload,
  }),
  gettyListMoodsSuccess: (payload: ListMoodsResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListMoodsSuccess' as const,
    payload,
  }),
  gettyListMoodsFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListMoodsFailure' as const,
    message,
  }),

  gettyListTourCategoriesRequest: (payload: Site.Ref) => ({
    domain: 'getty' as const,
    type: 'gettyListTourCategoriesRequest' as const,
    payload,
  }),
  gettyListTourCategoriesSuccess: (payload: ListTourCategoriesResponse.Entity, tours: ListToursResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListTourCategoriesSuccess' as const,
    payload,
    tours
  }),
  gettyListTourCategoriesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListTourCategoriesFailure' as const,
    message,
  }),
  gettyGetCarouselItemRequest: (payload: GetCarouselItemRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetCarouselItemRequest' as const,
    payload,
  }),
  gettyGetCarouselItemSuccess: (payload: GetCarouselItemResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetCarouselItemSuccess' as const,
    payload,
  }),
  gettyGetCarouselItemFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetCarouselItemFailure' as const,
    message,
  }),
  gettyListCarouselItemsRequest: (payload: ListCarouselItemsRequest.Entity, heroImageMedia?: Media.Ref) => ({
    domain: 'getty' as const,
    type: 'gettyListCarouselItemsRequest' as const,
    payload,
    heroImageMedia,
  }),
  gettyListCarouselItemsSuccess: (carouselItems: ReadonlyArray<CarouselItem.Entity>, mediaList?: ReadonlyArray<Media.Entity>) => ({
    domain: 'getty' as const,
    type: 'gettyListCarouselItemsSuccess' as const,
    carouselItems,
    mediaList,
  }),
  gettyListCarouselItemsFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListCarouselItemsFailure' as const,
    message,
  }),
  gettyListCarouselItemsReset: () => ({
    domain: 'getty' as const,
    type: 'gettyListCarouselItemsReset' as const,
  }),
  // }}} audio access.

  // {{{ audio management.
  gettyCreateStopRequest: (payload: CreateStopRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopRequest' as const,
    payload,
  }),
  gettyCreateStopSuccess: (payload: CreateStopResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopSuccess' as const,
    payload,
  }),
  gettyCreateStopFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopFailure' as const,
    message,
  }),
  gettyCreateStopReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopReset' as const,
  }),
  gettyUpdateStopRequest: (payload: UpdateStopRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopRequest' as const,
    payload,
  }),
  gettyUpdateStopSuccess: (stop: Stop.Entity, relatedStops?: ReadonlyArray<Stop.Entity>) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopSuccess' as const,
    stop,
    relatedStops
  }),
  gettyUpdateStopFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopFailure' as const,
    message,
  }),
  gettyUpdateStopReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopReset' as const,
  }),
  gettyDeleteStopRequest: (payload: DeleteStopRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopRequest' as const,
    payload,
  }),
  gettyDeleteStopSuccess: (payload: DeleteStopResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopSuccess' as const,
    payload,
  }),
  gettyDeleteStopFailure: (stopRef: Stop.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopFailure' as const,
    message,
    stopRef
  }),
  gettyDeleteStopReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopReset' as const,
  }),
  gettyCreateTourRequest: (payload: CreateTourRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourRequest' as const,
    payload,
  }),
  gettyCreateTourSuccess: (payload: CreateTourResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourSuccess' as const,
    payload,
  }),
  gettyCreateTourFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourFailure' as const,
    message,
  }),
  gettyDeleteTourRequest: (payload: DeleteTourRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourRequest' as const,
    payload,
  }),
  gettyDeleteTourReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourReset' as const,
  }),
  gettyDeleteTourSuccess: (payload: DeleteTourResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourSuccess' as const,
    payload,
  }),
  gettyDeleteTourFailure: (tourRef: Tour.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourFailure' as const,
    message,
    tourRef
  }),
  gettyUpdateTourRequest: (payload: UpdateTourRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourRequest' as const,
    payload,
  }),
  gettyUpdateTourSuccess: (payload: GettyUpdateTourSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourSuccess' as const,
    payload,
  }),
  gettyUpdateTourFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourFailure' as const,
    message,
  }),
  gettyUpdateTourReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourReset' as const,
  }),

  gettyCreateMoodRequest: (payload: CreateMoodRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateMoodRequest' as const,
    payload,
  }),
  gettyCreateMoodSuccess: (payload: CreateMoodResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateMoodSuccess' as const,
    payload,
  }),
  gettyCreateMoodFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateMoodFailure' as const,
    message,
  }),
  gettyDeleteMoodRequest: (payload: DeleteMoodRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteMoodRequest' as const,
    payload,
  }),
  gettyDeleteMoodReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteMoodReset' as const,
  }),
  gettyDeleteMoodSuccess: (payload: DeleteMoodResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteMoodSuccess' as const,
    payload,
  }),
  gettyDeleteMoodFailure: (moodRef: Mood.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteMoodFailure' as const,
    message,
    moodRef
  }),
  gettyUpdateMoodRequest: (payload: UpdateMoodRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateMoodRequest' as const,
    payload,
  }),
  gettyUpdateMoodSuccess: (payload: GettyUpdateMoodSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateMoodSuccess' as const,
    payload,
  }),
  gettyUpdateMoodFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateMoodFailure' as const,
    message,
  }),
  gettyUpdateMoodReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateMoodReset' as const,
  }),

  gettyCreateTourCategoryRequest: (payload: CreateTourCategoryRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourCategoryRequest' as const,
    payload,
  }),
  gettyCreateTourCategorySuccess: (payload: CreateTourCategoryResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourCategorySuccess' as const,
    payload,
  }),
  gettyCreateTourCategoryFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourCategoryFailure' as const,
    message,
  }),
  gettyCreateTourCategoryReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateTourCategoryReset' as const,
  }),
  gettyDeleteTourCategoryRequest: (payload: DeleteTourCategoryRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourCategoryRequest' as const,
    payload,
  }),
  gettyDeleteTourCategorySuccess: (payload: DeleteTourCategoryResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourCategorySuccess' as const,
    payload,
  }),
  gettyDeleteTourCategoryFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourCategoryFailure' as const,
    message,
  }),
  gettyDeleteTourCategoryReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteTourCategoryReset' as const,
  }),
  gettyUpdateTourCategoryRequest: (payload: UpdateTourCategoryRequest.Entity, fieldName: string) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoryRequest' as const,
    payload,
    fieldName,
  }),
  gettyUpdateTourCategorySuccess: (payload: UpdateTourCategoryResponse.Entity, fieldName: string, ref: TourCategory.Ref) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategorySuccess' as const,
    payload,
    fieldName,
    ref,
  }),
  gettyUpdateTourCategoryFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoryFailure' as const,
    message,
  }),
  gettyUpdateTourCategoryReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoryReset' as const,
  }),
  gettyUpdateTourCategoriesRequest: (payload: UpdateTourCategoriesRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoriesRequest' as const,
    payload,
  }),
  gettyUpdateTourCategoriesSuccess: (payload: UpdateTourCategoriesResponse.Entity, tours: ListToursResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoriesSuccess' as const,
    payload,
    tours,
  }),
  gettyUpdateTourCategoriesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateTourCategoriesFailure' as const,
    message,
  }),
  gettyCreateCarouselItemRequest: (payload: CreateCarouselItemRequest.Entity, stop: Stop.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateCarouselItemRequest' as const,
    payload,
    stop
  }),
  gettyCreateCarouselItemSuccess: (payload: CreateCarouselItemResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateCarouselItemSuccess' as const,
    payload,
  }),
  gettyCreateCarouselItemFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateCarouselItemFailure' as const,
    message,
  }),
  gettyCreateCarouselItemReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateCarouselItemReset' as const,
  }),
  gettyUpdateCarouselItemRequest: (payload: UpdateCarouselItemRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateCarouselItemRequest' as const,
    payload,
  }),
  gettyUpdateCarouselItemSuccess: (payload: UpdateCarouselItemResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateCarouselItemSuccess' as const,
    payload,
  }),
  gettyUpdateCarouselItemFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateCarouselItemFailure' as const,
    message,
  }),
  gettyUpdateCarouselItemReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateCarouselItemReset' as const,
  }),
  // }}} audio management.

  // {{{ publish access.
  gettyGetListBundleSummariesRequest: () => ({
    domain: 'getty' as const,
    type: 'gettyGetListBundleSummariesRequest' as const,
  }),
  gettyGetListBundleSummariesSuccess: (payload: ListBundleSummariesResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetListBundleSummariesSuccess' as const,
    payload,
  }),
  gettyGetListBundleSummariesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetListBundleSummariesFailure' as const,
    message,
  }),
  gettyGetListReleasesRequest: (payload: ListReleasesRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetListReleasesRequest' as const,
    payload,
  }),
  gettyGetListReleasesSuccess: (payload: ListReleasesResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetListReleasesSuccess' as const,
    payload,
  }),
  gettyGetListReleasesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetListReleasesFailure' as const,
    message,
  }),
  gettyGetListBundlesRequest: (payload: ListBundlesRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetListBundlesRequest' as const,
    payload,
  }),
  gettyGetListBundlesSuccess: (payload: ListBundlesResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetListBundlesSuccess' as const,
    payload,
  }),
  // gettyGetListBundlesFailure: (message: ErrorMessage) => ({
  //   domain: 'getty' as const,
  //   type: 'gettyGetListBundlesFailure' as const,
  //   message,
  // }),
  // }}} publish access.

  // {{{ publish management.
  gettyCreateReleaseRequest: (payload: CreateReleaseRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateReleaseRequest' as const,
    payload,
  }),
  gettyCreateReleaseSuccess: (payload: CreateReleaseResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateReleaseSuccess' as const,
    payload,
  }),
  gettyCreateReleaseFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateReleaseFailure' as const,
    message,
  }),
  gettyCreateReleaseReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateReleaseReset' as const,
  }),
  // }}} publish management.

  // {{{ validation access.
  gettyListValidationFailuresRequest: (payload: ListValidationFailuresRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListValidationFailuresRequest' as const,
    payload,
  }),
  gettyListValidationFailuresSuccess: (payload: ListValidationFailuresResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListValidationFailuresSuccess' as const,
    payload,
  }),
  gettyListValidationFailuresFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListValidationFailuresFailure' as const,
    message,
  }),
  gettyListAllValidationFailuresRequest: (payload: ListValidationFailuresRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListAllValidationFailuresRequest' as const,
    payload,
  }),
  gettyListAllValidationFailuresSuccess: (payload: ListAllValidationFailures) => ({
    domain: 'getty' as const,
    type: 'gettyListAllValidationFailuresSuccess' as const,
    payload,
  }),
  gettyListAllValidationFailuresFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListAllValidationFailuresFailure' as const,
    message,
  }),
  // }}} validation access.

  // {{{ visitor info access.
  gettyListVisitorInfoPagesRequest: (payload: Site.Ref) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorInfoPagesRequest' as const,
    payload,
  }),
  gettyListVisitorInfoPagesSuccess: (payload: GettyVisitorInfoSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorInfoPagesSuccess' as const,
    payload,
  }),
  gettyListVisitorInfoPagesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorInfoPagesFailure' as const,
    message,
  }),
  gettyGetVisitorInfoPageRequest: (payload: GetVisitorInfoPageRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorInfoPageRequest' as const,
    payload,
  }),
  gettyGetVisitorInfoPageSuccess: (payload: GetVisitorInfoPageResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorInfoPageSuccess' as const,
    payload,
  }),
  gettyGetVisitorInfoPageFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorInfoPageFailure' as const,
    message,
  }),
  gettyGetAmenityRequest: (payload: GetAmenityRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetAmenityRequest' as const,
    payload,
  }),
  gettyGetAmenitySuccess: (payload: {
    amenity: UpdateAmenityResponse.Entity['amenity'];
    wayfindingMaps?: AmentityUpdate['wayfindingMaps'];
  }) => ({
    domain: 'getty' as const,
    type: 'gettyGetAmenitySuccess' as const,
    payload,
  }),
  gettyGetAmenityFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetAmenityFailure' as const,
    message,
  }),
  // }}} visitor info access.

  // {{{ visitor info management.
  gettyCreateVisitorInfoPageRequest: (payload: CreateVisitorInfoPageRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorInfoPageRequest' as const,
    payload,
  }),
  gettyCreateVisitorInfoPageSuccess: (payload: CreateVisitorInfoPageResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorInfoPageSuccess' as const,
    payload,
  }),
  gettyCreateVisitorInfoPageFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorInfoPageFailure' as const,
    message,
  }),
  gettyCreateVisitorInfoPageReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorInfoPageReset' as const
  }),
  gettyDeleteVisitorInfoPageRequest: (payload: DeleteVisitorInfoPageRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorInfoPageRequest' as const,
    payload,
  }),
  gettyDeleteVisitorInfoPageSuccess: (payload: DeleteVisitorInfoPageResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorInfoPageSuccess' as const,
    payload,
  }),
  gettyDeleteVisitorInfoPageFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorInfoPageFailure' as const,
    message,
  }),
  gettyDeleteVisitorInfoPageReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorInfoPageReset' as const
  }),
  gettyCreateAmenityRequest: (payload: CreateAmenityRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateAmenityRequest' as const,
    payload,
  }),
  gettyCreateAmenitySuccess: (payload: CreateAmenityResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateAmenitySuccess' as const,
    payload,
  }),
  gettyCreateAmenityFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateAmenityFailure' as const,
    message,
  }),
  gettyCreateAmenityReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateAmenityReset' as const
  }),
  gettyDeleteAmenityRequest: (payload: DeleteAmenityRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteAmenityRequest' as const,
    payload,
  }),
  gettyDeleteAmenitySuccess: (payload: DeleteAmenityResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteAmenitySuccess' as const,
    payload,
  }),
  gettyDeleteAmenityFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteAmenityFailure' as const,
    message,
  }),
  gettyDeleteAmenityReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteAmenityReset' as const
  }),
  gettyUpdateVisitorInfoPageRequest: (payload: UpdateVisitorInfoPageRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoPageRequest' as const,
    payload,
  }),
  gettyUpdateVisitorInfoPageSuccess: (payload: UpdateVisitorInfoPageResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoPageSuccess' as const,
    payload,
  }),
  gettyUpdateVisitorInfoPageFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoPageFailure' as const,
    message,
  }),
  gettyUpdateAmenityRequest: (payload: UpdateAmenityRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateAmenityRequest' as const,
    payload,
  }),
  gettyUpdateAmenitySuccess: (payload: {
    amenity: UpdateAmenityResponse.Entity['amenity'];
    wayfindingMaps?: AmentityUpdate['wayfindingMaps'];
  }) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateAmenitySuccess' as const,
    payload,
  }),
  gettyUpdateAmenityFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateAmenityFailure' as const,
    message,
  }),
  gettyUpdateVisitorInfoSiteContentRequest: (payload: VisitorInfoPages) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoSiteContentRequest' as const,
    payload,
  }),
  gettyUpdateVisitorInfoSiteContentSuccess: (payload: UpdateVisitorInfoSiteContentResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoSiteContentSuccess' as const,
    payload,
  }),
  gettyUpdateVisitorInfoSiteContentFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorInfoSiteContentFailure' as const,
    message,
  }),
  // }}} visitor info management.

  // {{{ integration.
  gettyListTMSObjectsRequest: (payload: ListTMSObjectsRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListTMSObjectsRequest' as const,
    payload,
  }),
  gettyListTMSObjectsSuccess: (payload: ListTMSObjectsResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListTMSObjectsSuccess' as const,
    payload,
  }),
  gettyListTMSObjectFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListTMSObjectsFailure' as const,
    message,
  }),
  gettyListGetty360EventsRequest: () => ({
    domain: 'getty' as const,
    type: 'gettyListGetty360EventsRequest' as const,
  }),
  gettyListGetty360EventsSuccess: (payload: ListGetty360EventsResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListGetty360EventsSuccess' as const,
    payload,
  }),
  gettyListGetty360EventsFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListGetty360EventsFailure' as const,
    message,
  }),
  // }}} integration.

  // {{{ structure access.
  gettyListSitesRequest: () => ({
    domain: 'getty' as const,
    type: 'gettyListSitesRequest' as const,
  }),
  gettyListSitesSuccess: (payload: ListSitesResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListSitesSuccess' as const,
    payload,
  }),
  gettyListSitesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListSitesFailure' as const,
    message,
  }),
  // }}} structure access.

  // {{{ analytics.
  gettyGetAnalyticsRequest: () => ({
    domain: 'getty' as const,
    type: 'gettyGetAnalyticsRequest' as const,
  }),
  gettyGetAnalyticsSuccess: (payload: AnalyticsResponse) => ({
    domain: 'getty' as const,
    type: 'gettyGetAnalyticsSuccess' as const,
    payload,
  }),
  gettyGetAnalyticsFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetAnalyticsFailure' as const,
    message,
  }),
  // }}} analytics.

  gettyGetStopPointOfInterestRequest: (payload: GetStopPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopPointOfInterestRequest' as const,
    payload,
  }),
  gettyGetStopPointOfInterestSuccess: (
    stopPointOfInterest: StopPointOfInterest.Entity | undefined,
    resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>,
    baseEntity?: any,
    moods?: ReadonlyArray<Mood.Entity>,
    tours?: ReadonlyArray<Tour.Entity>
  ) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopPointOfInterestSuccess' as const,
    resolvedValues,
    baseEntity,
    stopPointOfInterest,
    moods,
    tours,
  }),
  gettyGetStopPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetStopPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateStopPointOfInterestRequest: (payload: CreateStopPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopPointOfInterestRequest' as const,
    payload,
  }),
  gettyCreateStopPointOfInterestSuccess: (payload: CreateStopPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopPointOfInterestSuccess' as const,
    payload,
  }),
  gettyCreateStopPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateStopPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateStopPointOfInterestReset' as const,
  }),
  gettyUpdateStopPointOfInterestRequest: (payload: UpdateStopPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopPointOfInterestRequest' as const,
    payload,
  }),
  gettyUpdateStopPointOfInterestSuccess: (stopPointOfInterest: StopPointOfInterest.Entity | undefined, resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>, baseEntity?: any,
    moods?: ReadonlyArray<Mood.Entity>,
    tours?: ReadonlyArray<Tour.Entity>) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopPointOfInterestSuccess' as const,
    resolvedValues,
    baseEntity,
    stopPointOfInterest,
    moods,
    tours,
  }),
  gettyUpdateStopPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopPointOfInterestFailure' as const,
    message,
  }),
  gettyUpdateStopPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateStopPointOfInterestReset' as const,
  }),
  gettyDeleteStopPointOfInterestRequest: (payload: DeleteStopPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopPointOfInterestRequest' as const,
    payload,
  }),
  gettyDeleteStopPointOfInterestSuccess: (payload: DeleteStopPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopPointOfInterestSuccess' as const,
    payload,
  }),
  gettyDeleteStopPointOfInterestFailure: (stopPointOfInterestRef: StopPointOfInterest.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopPointOfInterestFailure' as const,
    message,
    stopPointOfInterestRef
  }),
  gettyDeleteStopPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteStopPointOfInterestReset' as const,
  }),
  gettyGetGenericPointOfInterestRequest: (payload: GetGenericPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetGenericPointOfInterestRequest' as const,
    payload,
  }),
  gettyGetGenericPointOfInterestSuccess: (genericPointOfInterest: GenericPointOfInterest.Entity | undefined) => ({
    domain: 'getty' as const,
    type: 'gettyGetGenericPointOfInterestSuccess' as const,
    genericPointOfInterest,
  }),
  gettyGetGenericPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetGenericPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateGenericPointOfInterestRequest: (payload: CreateGenericPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateGenericPointOfInterestRequest' as const,
    payload,
  }),
  gettyCreateGenericPointOfInterestSuccess: (payload: CreateGenericPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateGenericPointOfInterestSuccess' as const,
    payload,
  }),
  gettyCreateGenericPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateGenericPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateGenericPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateGenericPointOfInterestReset' as const,
  }),
  gettyUpdateGenericPointOfInterestRequest: (payload: UpdateGenericPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateGenericPointOfInterestRequest' as const,
    payload,
  }),
  gettyUpdateGenericPointOfInterestSuccess: (genericPointOfInterest: GenericPointOfInterest.Entity | undefined) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateGenericPointOfInterestSuccess' as const,
    genericPointOfInterest,
  }),
  gettyUpdateGenericPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateGenericPointOfInterestFailure' as const,
    message,
  }),
  gettyUpdateGenericPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateGenericPointOfInterestReset' as const,
  }),
  gettyDeleteGenericPointOfInterestRequest: (payload: DeleteGenericPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteGenericPointOfInterestRequest' as const,
    payload,
  }),
  gettyDeleteGenericPointOfInterestSuccess: (payload: DeleteGenericPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteGenericPointOfInterestSuccess' as const,
    payload,
  }),
  gettyDeleteGenericPointOfInterestFailure: (genericPointOfInterestRef: GenericPointOfInterest.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteGenericPointOfInterestFailure' as const,
    message,
    genericPointOfInterestRef
  }),
  gettyDeleteGenericPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteGenericPointOfInterestReset' as const,
  }),
  gettyGetEventPointOfInterestRequest: (payload: GetEventPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetEventPointOfInterestRequest' as const,
    payload,
  }),
  gettyGetEventPointOfInterestSuccess: (eventPointOfInterest: EventPointOfInterest.Entity | undefined, resolvedValues?: ReadonlyArray<ResolvedValueResult.Entity>) => ({
    domain: 'getty' as const,
    type: 'gettyGetEventPointOfInterestSuccess' as const,
    resolvedValues,
    eventPointOfInterest,
  }),
  gettyGetEventPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetEventPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateEventPointOfInterestRequest: (payload: CreateEventPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateEventPointOfInterestRequest' as const,
    payload,
  }),
  gettyCreateEventPointOfInterestSuccess: (payload: CreateEventPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateEventPointOfInterestSuccess' as const,
    payload,
  }),
  gettyCreateEventPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateEventPointOfInterestFailure' as const,
    message,
  }),
  gettyCreateEventPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyCreateEventPointOfInterestReset' as const,
  }),
  gettyUpdateEventPointOfInterestRequest: (payload: UpdateEventPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateEventPointOfInterestRequest' as const,
    payload,
  }),
  gettyUpdateEventPointOfInterestSuccess: (payload: GettyUpdateEventPointOfInterestSuccess) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateEventPointOfInterestSuccess' as const,
    payload,
  }),
  gettyUpdateEventPointOfInterestFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateEventPointOfInterestFailure' as const,
    message,
  }),
  gettyUpdateEventPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyUpdateEventPointOfInterestReset' as const,
  }),
  gettyDeleteEventPointOfInterestRequest: (payload: DeleteEventPointOfInterestRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteEventPointOfInterestRequest' as const,
    payload,
  }),
  gettyDeleteEventPointOfInterestSuccess: (payload: DeleteEventPointOfInterestResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteEventPointOfInterestSuccess' as const,
    payload,
  }),
  gettyDeleteEventPointOfInterestFailure: (eventPointOfInterestRef: EventPointOfInterest.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteEventPointOfInterestFailure' as const,
    message,
    eventPointOfInterestRef
  }),
  gettyDeleteEventPointOfInterestReset: () => ({
    domain: 'getty' as const,
    type: 'gettyDeleteEventPointOfInterestReset' as const,
  }),
  gettyGetVisitorTypeRequest: (payload: GetVisitorTypeRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorTypeRequest' as const,
    payload,
  }),
  gettyGetVisitorTypeSuccess: (visitorType: VisitorType.Entity | undefined) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorTypeSuccess' as const,
    visitorType,
  }),
  gettyGetVisitorTypeFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyGetVisitorTypeFailure' as const,
    message,
  }),
  gettyCreateVisitorTypeRequest: (payload: CreateVisitorTypeRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorTypeRequest' as const,
    payload,
  }),
  gettyCreateVisitorTypeSuccess: (payload: CreateVisitorTypeResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorTypeSuccess' as const,
    payload,
  }),
  gettyCreateVisitorTypeFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyCreateVisitorTypeFailure' as const,
    message,
  }),
  gettyUpdateVisitorTypeRequest: (payload: UpdateVisitorTypeRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorTypeRequest' as const,
    payload,
  }),
  gettyUpdateVisitorTypeSuccess: (visitorType: VisitorType.Entity | undefined) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorTypeSuccess' as const,
    visitorType,
  }),
  gettyUpdateVisitorTypeFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyUpdateVisitorTypeFailure' as const,
    message,
  }),
  gettyDeleteVisitorTypeRequest: (payload: DeleteVisitorTypeRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorTypeRequest' as const,
    payload,
  }),
  gettyDeleteVisitorTypeSuccess: (payload: DeleteVisitorTypeResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorTypeSuccess' as const,
    payload,
  }),
  gettyDeleteVisitorTypeFailure: (visitorTypeRef: VisitorType.Ref, message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyDeleteVisitorTypeFailure' as const,
    message,
    visitorTypeRef
  }),
  gettyListVisitorTypesRequest: (payload: ListVisitorTypesRequest.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorTypesRequest' as const,
    payload,
  }),
  gettyListVisitorTypesSuccess: (payload: ListVisitorTypesResponse.Entity) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorTypesSuccess' as const,
    payload,
  }),
  gettyListVisitorTypesFailure: (message: ErrorMessage) => ({
    domain: 'getty' as const,
    type: 'gettyListVisitorTypesFailure' as const,
    message,
  }),
}

export type GettyActionCreators = typeof actionCreators;
export type GettyAction = ReturnType<typeof actionCreators[keyof typeof actionCreators]>;
export type GettyActions = {
  [T in keyof GettyActionCreators]: ReturnType<GettyActionCreators[T]>;
};

const assertGetty = (domain: string) => {
  return domain === 'getty'
}

export const gettyReducer = (state: GettyAppState = initialGettyAppState, action: GettyAction): GettyAppState => {
  if (!assertGetty(action.domain)) {
    return state
  }

  switch (action.type) {
    // {{{ audio access.
    case 'gettyGetStopRequest': {
      return {
        ...state,
        stopUpdate: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyGetStopSuccess': {
      const { baseEntity, fieldMappings, resolvedValues, relatedStops, stop, tours, moods } = action

      if (!stop) {
        throw new Invariant('[ gettyGetStopSuccess ] Missing `stop` in response payload')
      }

      return {
        ...state,
        stopUpdate: {
          data: {
            stop,
            baseEntity,
            fieldMappings,
            relatedStops,
            resolvedValues,
            tours,
            moods,
          },
          errors: [],
          status: Status.Ready,
        },
      }
    }
    case 'gettyGetStopFailure': {
      return {
        ...state,
        stopUpdate: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyListStopsRequest':
    case 'gettyListStopsSearchRequest': {
      const { page } = action.payload

      // if we are paginating, set to updating so we can update the resultset.
      if (state.stops.status === Status.Ready || state.stops.status === Status.Updating) {
        if (page) {
          return {
            ...state,
            stops: {
              data: state.stops.data,
              status:  Status.Updating,
            },
          }
        }
      }

      // otherwise this is a new resultset.
      return {
        ...state,
        stops: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyListStopsSuccess': {
      const { stops, nextPage, totalItems, cumulativePaginationEnabled } = action.payload

      const data: PaginatedList<Stop.Entity> =  {
        entityList: cumulativePaginationEnabled && state.stops.status === Status.Updating ?
          [ ...state.stops.data.entityList, ...stops ] : stops,
        nextPage: nextPage || undefined,
        totalItems,
      }

      return {
        ...state,
        stops: {
          data,
          errors: [],
          status: Status.Ready,
        },
      }
    }
    case 'gettyListStopsFailure': {
      return {
        ...state,
        stops: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyListStopsReset': {
      return {
        ...state,
        stops: {
          status: Status.Idle,
        }
      }
    }

    case 'gettyGetTourRequest':
      return {
        ...state,
        tourUpdate: { status: Status.Loading }
      }

    case 'gettyGetTourSuccess': {
      return {
        ...state,
        tourUpdate: { status: Status.Ready, data: { tour: action.payload.tour } }
      }
    }

    case 'gettyGetTourFailure': {
      return {
        ...state,
        tours: { status: Status.Failed, messages: [action.message] }
      }
    }

    case 'gettyListToursRequest': {
      return {
        ...state,
        tours: { status: Status.Loading }
      }
    }

    case 'gettyListToursSuccess': {
      return {
        ...state,
        tours: {
          status: Status.Ready,
          data: action.payload.tours,
        }
      }
    }

    case 'gettyListToursFailure': {
      return {
        ...state,
        tours: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyGetMoodRequest':
      return {
        ...state,
        moodUpdate: { status: Status.Loading }
      }

    case 'gettyGetMoodSuccess': {
      return {
        ...state,
        moodUpdate: { status: Status.Ready, data: { mood: action.payload.mood } }
      }
    }

    case 'gettyGetMoodFailure': {
      return {
        ...state,
        moods: { status: Status.Failed, messages: [action.message] }
      }
    }

    case 'gettyListMoodsRequest': {
      return {
        ...state,
        moods: { status: Status.Loading }
      }
    }

    case 'gettyListMoodsSuccess': {
      return {
        ...state,
        moods: {
          status: Status.Ready,
          data: action.payload.moods,
        }
      }
    }

    case 'gettyListMoodsFailure': {
      return {
        ...state,
        moods: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyListTourCategoriesRequest': {
      return {
        ...state,
        categories: { status: Status.Loading }
      }
    }

    case 'gettyListTourCategoriesSuccess': {
      const { tourCategories } = action.payload
      const { tours } = action.tours
      return {
        ...state,
        categories: {
          status: Status.Ready,
          data:{
            tourCategories,
            tours,
          }
        }
      }
    }

    case 'gettyListTourCategoriesFailure': {
      return {
        ...state,
        categories: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyGetCarouselItemRequest': {
      return {
        ...state,
        carouselItemUpdate: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyGetCarouselItemSuccess': {
      const { carouselItem } = action.payload

      if (!carouselItem) {
        throw new Invariant('[ gettyGetCarouselItemSuccess ] Missing `carouselItem` in response payload')
      }

      return {
        ...state,
        carouselItemUpdate: {
          status:  Status.Ready,
          data: carouselItem,
        },
      }
    }
    case 'gettyGetCarouselItemFailure': {
      return {
        ...state,
        carouselItems: {
          status:  Status.Failed,
          messages: [action.message]
        },
      }
    }

    case 'gettyListCarouselItemsRequest': {
      return {
        ...state,
        carouselItems: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyListCarouselItemsSuccess': {
      const { carouselItems, mediaList } = action
      return {
        ...state,
        carouselItems: {
          status:  Status.Ready,
          data: {
            carouselItems,
            mediaList
          }
        },
      }
    }
    case 'gettyListCarouselItemsFailure': {
      return {
        ...state,
        carouselItems: {
          status:  Status.Failed,
          messages: [action.message]
        },
      }
    }
    case 'gettyListCarouselItemsReset': {
      return {
        ...state,
        carouselItems: {
          status:  Status.Idle,
        },
      }
    }
    // }}} audio access.

    // {{{ audio management
    case 'gettyCreateStopRequest': {
      return {
        ...state,
        stopCreate: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyCreateStopSuccess': {
      if (!action.payload.stop) {
        throw new Invariant('[ gettyCreateStopSuccess ] Missing `stop` in response payload')
      }

      return {
        ...state,
        stopCreate: {
          status: Status.Ready,
          data: action.payload.stop,
        }
      }
    }
    case 'gettyCreateStopFailure': {
      return {
        ...state,
        stopCreate: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    case 'gettyCreateStopReset': {
      return {
        ...state,
        stopCreate: {
          status: Status.Idle,
        }
      }
    }

    case 'gettyUpdateStopRequest': {
      if (state.stopUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateStopRequest ] Cannot update entity when `stopUpdate` is not in Ready state')
      }

      return {
        ...state,
        stopUpdate: {
          ...state.stopUpdate,
          status: Status.Updating,
        },
      }
    }
    case 'gettyUpdateStopSuccess': {
      const { stop, relatedStops } = action
      if (!stop) {
        throw new Invariant('[ gettyUpdateStopSuccess ] Missing `stop` in response payload')
      }

      return {
        ...state,
        stopUpdate: {
          status: Status.Ready,
          data: {
            // linked field mappings and field values may exist, so allow these to persist.
            ...(state.stopUpdate.status === Status.Updating ? state.stopUpdate.data : {}),
            stop,
            relatedStops
          },
        }
      }
    }
    case 'gettyUpdateStopFailure': {
      if (state.stopUpdate.status !== Status.Updating) {
        throw new Error('[ gettyUpdateStopFailure ] Cannot update entity when `stopUpdate` is not in Updating state')
      }
      return {
        ...state,
        stopUpdate: {
          data: state.stopUpdate.data,
          status: Status.Stale,

        }
      }
    }
    case 'gettyUpdateStopReset': {
      return {
        ...state,
        stopUpdate: state.stopUpdate.status === Status.Stale ? {
          data: state.stopUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }

    case 'gettyDeleteStopRequest': {
      return {
        ...state,
        stopDelete: {
          ...state.stopDelete,
          status: Status.Loading,
          ref: Stop.mustRef(action.payload.stopRef)
        }
      }
    }
    case 'gettyDeleteStopSuccess': {
      return {
        ...state,
        stopDelete: {
          message: 'Stop deleted',
          status: Status.Complete,
          ref: Stop.mustRef(action.payload.ref)
        }
      }
    }
    case 'gettyDeleteStopFailure': {
      return {
        ...state,
        stopDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.stopRef
        }
      }
    }
    case 'gettyDeleteStopReset': {
      return {
        ...state,
        stopDelete: {
          status: Status.Idle
        }
      }
    }

    case 'gettyCreateTourRequest': {
      return {
        ...state,
        tourCreate: { status: Status.Loading }
      }
    }
    case 'gettyCreateTourSuccess': {
      if (!action.payload.tour) {
        throw new Invariant('[ gettyCreateTourSuccess ] Missing `tour` in response payload')
      }
      const { tour } = action.payload
      return {
        ...state,
        tourCreate: {
          status: Status.Complete,
          message: 'Tour created',
        },
        tourUpdate: {
          ...state.tourUpdate,
          status: Status.Stale,
          data: {
            tour,
          }
        }
      }
    }
    case 'gettyCreateTourFailure': {
      return {
        ...state,
        tourCreate: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyDeleteTourRequest': {
      return {
        ...state,
        tourDelete: {
          status: Status.Loading,
          ref: Tour.mustRef(action.payload.tourRef)
        }
      }
    }
    case 'gettyDeleteTourReset': {
      return {
        ...state,
        tourDelete: { status: Status.Idle }
      }
    }
    case 'gettyDeleteTourSuccess': {
      return {
        ...state,
        tourDelete: {
          ref: Tour.mustRef(action.payload.ref),
          message: 'Tour deleted',
          status: Status.Complete
        }
      }
    }
    case 'gettyDeleteTourFailure': {
      return {
        ...state,
        tourDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.tourRef
        }
      }
    }

    case 'gettyUpdateTourRequest': {
      if (state.tourUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateTourRequest ] Cannot update entity when `tourUpdate` is not in Ready state')
      }

      return {
        ...state,
        tourUpdate: {
          ...state.tourUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateTourSuccess': {
      if (!action.payload.tour) {
        throw new Invariant('[ gettyUpdateTourSuccess ] Missing `tour` in response payload')
      }
      const { tour } = action.payload
      return {
        ...state,
        tourUpdate: {
          status: Status.Ready,
          data: { tour },
        }
      }
    }
    case 'gettyUpdateTourFailure': {
      return {
        ...state,
        tourUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyUpdateTourReset': {
      return {
        ...state,
        tourUpdate: state.tourUpdate.status === Status.Stale ? {
          data: state.tourUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }

    case 'gettyCreateMoodRequest': {
      return {
        ...state,
        moodCreate: { status: Status.Loading }
      }
    }
    case 'gettyCreateMoodSuccess': {
      if (!action.payload.mood) {
        throw new Invariant('[ gettyCreateMoodSuccess ] Missing `mood` in response payload')
      }
      const { mood } = action.payload
      return {
        ...state,
        moodCreate: {
          status: Status.Complete,
          message: 'Mood journey created',
        },
        moodUpdate: {
          ...state.moodUpdate,
          status: Status.Stale,
          data: {
            mood,
          }
        }
      }
    }
    case 'gettyCreateMoodFailure': {
      return {
        ...state,
        moodCreate: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyDeleteMoodRequest': {
      return {
        ...state,
        moodDelete: {
          status: Status.Loading,
          ref: Mood.mustRef(action.payload.moodRef)
        }
      }
    }
    case 'gettyDeleteMoodReset': {
      return {
        ...state,
        moodDelete: { status: Status.Idle }
      }
    }
    case 'gettyDeleteMoodSuccess': {
      return {
        ...state,
        moodDelete: {
          ref: Mood.mustRef(action.payload.ref),
          message: 'Mood journey deleted',
          status: Status.Complete
        }
      }
    }
    case 'gettyDeleteMoodFailure': {
      return {
        ...state,
        moodDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.moodRef
        }
      }
    }

    case 'gettyUpdateMoodRequest': {
      if (state.moodUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateMoodRequest ] Cannot update entity when `moodUpdate` is not in Ready state')
      }

      return {
        ...state,
        moodUpdate: {
          ...state.moodUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateMoodSuccess': {
      if (!action.payload.mood) {
        throw new Invariant('[ gettyUpdateMoodSuccess ] Missing `mood` in response payload')
      }
      const { mood } = action.payload
      return {
        ...state,
        moodUpdate: {
          status: Status.Ready,
          data: { mood },
        }
      }
    }
    case 'gettyUpdateMoodFailure': {
      return {
        ...state,
        moodUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyUpdateMoodReset': {
      return {
        ...state,
        moodUpdate: state.moodUpdate.status === Status.Stale ? {
          data: state.moodUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }

    case 'gettyGetVisitorTypeRequest':
      return {
        ...state,
        visitorTypeUpdate: { status: Status.Loading }
      }

    case 'gettyGetVisitorTypeSuccess': {
      const { visitorType } = action

      if (!visitorType) {
        throw new Invariant('[ gettyGetVisitorTypeSuccess ] Missing `visitorType` in response payload')
      }

      return {
        ...state,
        visitorTypeUpdate: { status: Status.Ready, data: { visitorType } }
      }
    }

    case 'gettyGetVisitorTypeFailure': {
      return {
        ...state,
        visitorTypes: { status: Status.Failed, messages: [action.message] }
      }
    }

    case 'gettyListVisitorTypesRequest': {
      return {
        ...state,
        visitorTypes: { status: Status.Loading }
      }
    }

    case 'gettyListVisitorTypesSuccess': {
      return {
        ...state,
        visitorTypes: {
          status: Status.Ready,
          data: action.payload.visitorTypes,
        }
      }
    }

    case 'gettyListVisitorTypesFailure': {
      return {
        ...state,
        visitorTypes: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyCreateVisitorTypeRequest': {
      return {
        ...state,
        visitorTypeCreate: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyCreateVisitorTypeSuccess': {
      if (!action.payload.visitorType) {
        throw new Invariant('[ gettyCreateVisitorTypeSuccess ] Missing `visitor type` in response payload')
      }

      return {
        ...state,
        visitorTypeCreate: {
          status: Status.Ready,
          data: action.payload.visitorType,
        }
      }
    }
    case 'gettyCreateVisitorTypeFailure': {
      return {
        ...state,
        visitorTypeCreate: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    case 'gettyUpdateVisitorTypeRequest': {
      if (state.visitorTypeUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateVisitorTypeRequest ] Cannot update entity when `visitorTypeUpdate` is not in Ready state')
      }

      return {
        ...state,
        visitorTypeUpdate: {
          ...state.visitorTypeUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateVisitorTypeSuccess': {
      if (!action.visitorType) {
        throw new Invariant('[ gettyUpdateVisitorTypeSuccess ] Missing `visitorType` in response payload')
      }
      const { visitorType } = action
      return {
        ...state,
        visitorTypeUpdate: {
          status: Status.Ready,
          data: { visitorType },
        }
      }
    }
    case 'gettyUpdateVisitorTypeFailure': {
      return {
        ...state,
        visitorTypeUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyDeleteVisitorTypeRequest': {
      return {
        ...state,
        visitorTypeDelete: {
          ...state.visitorTypeDelete,
          status: Status.Loading,
          ref: VisitorType.mustRef(action.payload.visitorTypeRef)
        }
      }
    }
    case 'gettyDeleteVisitorTypeSuccess': {
      return {
        ...state,
        visitorTypeDelete: {
          message: 'Visitor Type deleted',
          status: Status.Complete,
          ref: VisitorType.mustRef(action.payload.ref)
        }
      }
    }
    case 'gettyDeleteVisitorTypeFailure': {
      return {
        ...state,
        visitorTypeDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.visitorTypeRef
        }
      }
    }

    case 'gettyCreateTourCategoryRequest': {
      if (state.categories.status !== Status.Ready && state.categoryCreate.status !== Status.Idle) {
        throw new Error('[ gettyCreateTourCategoryRequest ] Cannot create entity when `categoryCreate` is not in Ready state')
      }
      return {
        ...state,
        categoryCreate: {
          status: Status.Loading,
        },
      }
    }

    case 'gettyCreateTourCategorySuccess': {
      if (!action.payload.tourCategory) {
        throw new Invariant('[ gettyCreateTourCategorySuccess ] Missing `tourCategory` in response payload')
      }
      if (state.categories.status !== Status.Ready) {
        throw new Error('[ gettyCreateTourCategorySuccess ] Cannot create entity when `categories` is not in Ready state')
      }
      const { tourCategory } = action.payload
      return {
        ...state,
        categoryCreate: {
          status: Status.Complete,
          message: 'Category created',
        },
        categories: {
          ...state.categories,
          status: Status.Ready,
          data: {
            ...state.categories.data,
            tourCategories: [...state.categories.data.tourCategories, tourCategory],
          }
        }
      }
    }

    case 'gettyCreateTourCategoryFailure': {
      return {
        ...state,
        categoryCreate: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyCreateTourCategoryReset': {
      return {
        ...state,
        categoryCreate: {
          status: Status.Idle,
        }
      }
    }

    case 'gettyDeleteTourCategoryRequest': {
      if (state.categories.status !== Status.Ready && state.categoryDelete.status !== Status.Idle) {
        throw new Error('[ gettyDeleteTourCategoryRequest ] Cannot delete entity when `categoryDelete` is not in Idle state')
      }
      return {
        ...state,
        categoryDelete: {
          status: Status.Loading,
        },
      }
    }

    case 'gettyDeleteTourCategorySuccess': {
      if (state.categories.status !== Status.Ready) {
        throw new Error('[ gettyDeleteTourCategorySuccess ] Cannot delete entity when `categories` is not in Ready state')
      }
      const tourCategories = state.categories.data.tourCategories.filter(category => {
        const id = category.ref && category.ref.id
        const currId = action.payload.ref && action.payload.ref.id
        return id !== currId
      })
      return {
        ...state,
        categoryDelete: {
          status: Status.Complete,
          message: 'Category deleted',
        },
        categories: {
          ...state.categories,
          status: Status.Ready,
          data: {
            ...state.categories.data,
            tourCategories,
          }
        }
      }
    }

    case 'gettyDeleteTourCategoryFailure': {
      return {
        ...state,
        categoryDelete: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyDeleteTourCategoryReset': {
      return {
        ...state,
        categoryDelete: {
          status: Status.Idle,
        }
      }
    }

    case 'gettyUpdateTourCategoryRequest': {
      if (state.categories.status !== Status.Ready) {
        throw new Error('[ gettyUpdateTourCategoryRequest ] Cannot update entity when `categories` is not in Ready state')
      }
      return {
        ...state,
        categoryUpdate: {
          status: Status.Loading,
        },
      }
    }

    case 'gettyUpdateTourCategorySuccess': {
      const { payload, ref } = action
      if (state.categories.status !== Status.Ready) {
        throw new Error('[ gettyUpdateTourCategorySuccess ] Cannot update entity when `categories` is not in Ready state')
      }
      const categoriesUpdate = state.categories.data.tourCategories.map(category => {
        return {
          ...category,
          ...category.ref && category.ref.id === ref.id && payload.tourCategory ? { name: payload.tourCategory.name } : null
        }
      })
      return {
        ...state,
        categoryUpdate: {
          status: Status.Complete,
          message: 'Category updated',
        },
        categories: {
          status: Status.Ready,
          data: {
            ...state.categories.data,
            tourCategories: categoriesUpdate,
          }
        }
      }
    }

    case 'gettyUpdateTourCategoryFailure': {
      return {
        ...state,
        categoryUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyUpdateTourCategoryReset': {
      return {
        ...state,
        categoryUpdate: {
          status: Status.Idle,
        },
      }
    }

    case 'gettyUpdateTourCategoriesRequest': {
      if (state.categories.status !== Status.Ready) {
        throw new Error('[ gettyUpdateTourCategoriesRequest ] Cannot update entity when `categories` is not in Ready state')
      }
      return {
        ...state,
        categories: {
          ...state.categories,
          status: Status.Updating,
        },
      }
    }

    case 'gettyUpdateTourCategoriesSuccess': {
      if (!action.payload.tourCategories) {
        throw new Invariant('[ gettyUpdateTourCategoriesSuccess ] Missing `tourCategories` in response payload')
      }

      const { tourCategories } = action.payload
      const { tours } = action.tours
      return {
        ...state,
        categories: {
          status: Status.Ready,
          data: {
            tours,
            tourCategories
          }
        }
      }
    }

    case 'gettyUpdateTourCategoriesFailure': {
      return {
        ...state,
        categories: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyCreateCarouselItemRequest': {
      return {
        ...state,
        carouselItemCreate: {
          status: Status.Loading
        }
      }
    }
    case 'gettyCreateCarouselItemSuccess': {
      if (!action.payload.carouselItem) {
        throw new Invariant('[ gettyCreateCarouselItemSuccess ] Missing `carouselItem` in response payload')
      }

      const { carouselItem } = action.payload
      return {
        ...state,
        carouselItemCreate: {
          status: Status.Complete,
          message: 'Carousel item created'
        },
        carouselItemUpdate: {
          status: Status.Ready,
          data: carouselItem
        }
      }

    }
    case 'gettyCreateCarouselItemFailure': {
      return {
        ...state,
        carouselItemCreate: {
          status: Status.Failed,
          messages: [action.message]
        }
      }
    }
    case 'gettyCreateCarouselItemReset': {
      return{
        ...state,
        carouselItemCreate: {
          status: Status.Idle
        }
      }
    }

    case 'gettyUpdateCarouselItemRequest': {
      if (state.carouselItemUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateCarouselItemRequest ] Cannot update entity when `carouselItemUpdate` is not in Ready state')
      }
      return {
        ...state,
        carouselItemUpdate: {
          ...state.carouselItemUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateCarouselItemSuccess': {
      const { carouselItem } = action.payload

      if (!carouselItem) {
        throw new Invariant('[ gettyUpdateCarouselItemSuccess ] Missing `carouselItem` in response payload')
      }

      return {
        ...state,
        carouselItemUpdate: {
          status: Status.Ready,
          data: carouselItem
        }

      }
    }
    case 'gettyUpdateCarouselItemFailure': {
      return {
        ...state,
        carouselItemCreate: {
          status: Status.Failed,
          messages: [action.message]
        }
      }
    }
    case 'gettyUpdateCarouselItemReset': {
      return {
        ...state,
        carouselItemUpdate: {
          status: Status.Idle
        }
      }
    }
    // }}} audio management.
    // {{{ publish access.
    case 'gettyGetListBundleSummariesRequest': {
      return {
        ...state,
        publishBundleSummaries: { status: Status.Loading }
      }
    }
    case 'gettyGetListBundleSummariesSuccess': {
      if (!action.payload.summaries) {
        throw new Invariant('[ gettyGetListBundleSummariesSuccess ] Missing `summaries` in response payload')
      }
      const { summaries } = action.payload
      return {
        ...state,
        publishBundleSummaries: {
          status: Status.Ready,
          data: summaries,
        },
      }
    }
    case 'gettyGetListBundleSummariesFailure': {
      return {
        ...state,
        publishBundleSummaries: { status: Status.Failed, messages: [action.message]  }
      }
    }

    case 'gettyGetListReleasesRequest': {
      return {
        ...state,
        publishReleases: { status: Status.Loading }
      }
    }

    case 'gettyGetListReleasesSuccess': {
      if (!action.payload.releases) {
        throw new Invariant('[ gettyGetListReleasesSuccess ] Missing `releases` in response payload')
      }
      return {
        ...state,
        publishReleases: {
          status: Status.Ready,
          data: action.payload.releases,
        },
      }
    }
    case 'gettyGetListReleasesFailure': {
      return {
        ...state,
        publishReleases: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyGetListBundlesRequest': {
      if(state.publishBundlesList.status === Status.Ready) {
        return {
          ...state,
          publishBundlesList: {
            status: Status.Updating,
            data: state.publishBundlesList.data
          }
        }
      }
      return {
        ...state,
        publishBundlesList: {
          status: Status.Loading
        }
      }
    }
    case 'gettyGetListBundlesSuccess': {
      if (!action.payload.bundles) {
        throw new Invariant('[ gettyGetListBundlesSuccess ] Missing `bundles` in response payload')
      }
      return {
        ...state,
        publishBundlesList: {
          status: Status.Ready,
          data: action.payload.bundles
        }
      }
    }
    // case 'gettyGetListBundlesFailure': {
    //   return {
    //     ...state,
    //     publishBundlesList: {
    //       status: Status.Failed,
    //       messages: [action.message]
    //     }
    //   }
    // }
    // }}} publish access.

    // {{{ publish management.
    case 'gettyCreateReleaseRequest': {
      return {
        ...state,
        publishReleaseCreate: {
          status: Status.Loading
        }
      }
    }
    case 'gettyCreateReleaseSuccess': {
      return {
        ...state,
        publishReleaseCreate: {
          status: Status.Ready,
          data: action.payload
        }

      }
    }
    case 'gettyCreateReleaseFailure': {
      return {
        ...state,
        publishReleaseCreate: {
          status: Status.Failed,
          messages: [action.message]
        }
      }
    }
    case 'gettyCreateReleaseReset': {
      return {
        ...state,
        publishReleaseCreate: {
          status: Status.Idle
        }
      }
    }
    // }}} publish management.

    // {{{ validation access.
    case 'gettyListValidationFailuresRequest': {
      return {
        ...state,
        validationFailuresList: {
          status: Status.Loading
        }
      }
    }

    case 'gettyListValidationFailuresSuccess': {
      if (!action.payload.failures) {
        throw new Error('Failures Array missing from response');
      }
      return {
        ...state,
        validationFailuresList: {
          status: Status.Ready,
          data: action.payload.failures
        }
      }
    }

    case 'gettyListValidationFailuresFailure': {
      return {
        ...state,
        validationFailuresList: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    case 'gettyListAllValidationFailuresRequest': {
      if(state.allValidationFailuresList.status === Status.Ready) {
        return {
          ...state,
          allValidationFailuresList: {
            status: Status.Updating,
            data: state.allValidationFailuresList.data
          }
        }
      }
      return {
        ...state,
        allValidationFailuresList: {
          status: Status.Loading
        }
      }
    }

    case 'gettyListAllValidationFailuresSuccess': {
      return {
        ...state,
        allValidationFailuresList: {
          status: Status.Ready,
          data: action.payload
        }
      }
    }

    case 'gettyListAllValidationFailuresFailure': {
      return {
        ...state,
        allValidationFailuresList: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    // }}} validation access.

    // {{{ visitor info access.
    case 'gettyListVisitorInfoPagesRequest': {
      if(state.visitorInfo.status === Status.Ready) {
        return {
          ...state,
          visitorInfo: {
            status: Status.Updating,
            data: state.visitorInfo.data
          }
        }
      }
      return {
        ...state,
        visitorInfo: {
          status: Status.Loading
        }
      }
    }

    case 'gettyListVisitorInfoPagesSuccess': {
      const { pages, siteContentRef } = action.payload
      return {
        ...state,
        visitorInfo: {
          status: Status.Ready,
          data: {
            pages,
            siteContentRef,
          }
        }
      }
    }

    case 'gettyListVisitorInfoPagesFailure': {
      return {
        ...state,
        visitorInfo: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyGetVisitorInfoPageRequest': {
      return {
        ...state,
        visitorInfoUpdate: { status: Status.Loading },
      }
    }

    case 'gettyGetVisitorInfoPageSuccess': {
      if (!action.payload.visitorInfoPage) {
        throw new Invariant('[ gettyGetVisitorInfoPageSuccess ] Missing `visitorInfoPage` in response payload')
      }
      return {
        ...state,
        visitorInfoUpdate: {
          status: Status.Ready,
          data: action.payload.visitorInfoPage
        }
      }
    }

    case 'gettyGetVisitorInfoPageFailure': {
      return {
        ...state,
        visitorInfoUpdate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyGetAmenityRequest': {
      return {
        ...state,
        amenityUpdate: { status: Status.Loading },
      }
    }

    case 'gettyGetAmenitySuccess': {
      if (!action.payload.amenity) {
        throw new Invariant('[ gettyGetAmenitySuccess ] Missing `amenity` in response payload')
      }

      const { amenity } = action.payload

      return {
        ...state,
        amenityUpdate: {
          status: Status.Ready,
          data: {
            ...action.payload,
            amenity,
          }
        }
      }
    }

    case 'gettyGetAmenityFailure': {
      return {
        ...state,
        amenityUpdate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    // }}} visitor info access.

    // {{{ visitor info management.
    case 'gettyCreateVisitorInfoPageRequest': {
      return {
        ...state,
        visitorInfoCreate: { status: Status.Loading },
      }
    }

    case 'gettyCreateVisitorInfoPageSuccess': {
      if (!action.payload.visitorInfoPage) {
        throw new Invariant('[ gettyCreateVisitorInfoPageSuccess ] Missing `visitorInfoPage` in response payload')
      }
      return {
        ...state,
        visitorInfoCreate: {
          status: Status.Ready,
          data: action.payload.visitorInfoPage
        }
      }
    }

    case 'gettyCreateVisitorInfoPageFailure': {
      return {
        ...state,
        visitorInfoCreate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    case 'gettyCreateVisitorInfoPageReset': {
      return {
        ...state,
        visitorInfoCreate: { status: Status.Idle },
      }
    }
    case 'gettyDeleteVisitorInfoPageRequest': {
      return {
        ...state,
        visitorInfoDelete: { status: Status.Loading },
      }
    }

    case 'gettyDeleteVisitorInfoPageSuccess': {
      return {
        ...state,
        visitorInfoDelete: {
          status: Status.Ready,
          data: VisitorInfoPage.mustRef(action.payload.ref)
        }
      }
    }

    case 'gettyDeleteVisitorInfoPageFailure': {
      return {
        ...state,
        visitorInfoDelete: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    case 'gettyDeleteVisitorInfoPageReset': {
      return {
        ...state,
        visitorInfoDelete: { status: Status.Idle },
      }
    }
    case 'gettyCreateAmenityRequest': {
      return {
        ...state,
        amenityCreate: { status: Status.Loading },
      }
    }

    case 'gettyCreateAmenitySuccess': {
      if (!action.payload.amenity) {
        throw new Invariant('[ gettyCreateAmenitySuccess ] Missing `amenity` in response payload')
      }
      return {
        ...state,
        amenityCreate: {
          status: Status.Ready,
          data: action.payload.amenity
        }
      }
    }

    case 'gettyCreateAmenityFailure': {
      return {
        ...state,
        amenityCreate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    case 'gettyCreateAmenityReset': {
      return {
        ...state,
        amenityCreate: { status: Status.Idle },
      }
    }
    case 'gettyDeleteAmenityRequest': {
      return {
        ...state,
        amenityDelete: { status: Status.Loading },
      }
    }

    case 'gettyDeleteAmenitySuccess': {
      return {
        ...state,
        amenityDelete: {
          status: Status.Ready,
          data: Amenity.mustRef(action.payload.ref)
        }
      }
    }

    case 'gettyDeleteAmenityFailure': {
      return {
        ...state,
        amenityDelete: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    case 'gettyDeleteAmenityReset': {
      return {
        ...state,
        amenityDelete: { status: Status.Idle },
      }
    }
    case 'gettyUpdateVisitorInfoPageRequest': {
      return {
        ...state,
        visitorInfoUpdate: { status: Status.Loading },
      }
    }

    case 'gettyUpdateVisitorInfoPageSuccess': {
      if (!action.payload.visitorInfoPage) {
        throw new Invariant('[ gettyUpdateVisitorInfoPageSuccess ] Missing `visitorInfoPage` in response payload')
      }
      return {
        ...state,
        visitorInfoUpdate: {
          status: Status.Ready,
          data: action.payload.visitorInfoPage
        }
      }
    }

    case 'gettyUpdateVisitorInfoPageFailure': {
      return {
        ...state,
        visitorInfoUpdate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyUpdateAmenityRequest': {
      return {
        ...state,
        amenityUpdate: state.amenityUpdate.status === Status.Ready ? {
          ...state.amenityUpdate,
          status: Status.Updating,
        } : {
          status: Status.Loading,
        },
      }
    }

    case 'gettyUpdateAmenitySuccess': {
      if (!action.payload.amenity) {
        throw new Invariant('[ gettyUpdateAmenitySuccess ] Missing `amenity` in response payload')
      }

      const { amenity } = action.payload

      return {
        ...state,
        amenityUpdate: {
          status: Status.Ready,
          data: {
            ...action.payload,
            amenity,
          }
        }
      }
    }

    case 'gettyUpdateAmenityFailure': {
      return {
        ...state,
        amenityUpdate: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }

    case 'gettyUpdateVisitorInfoSiteContentRequest': {
      if (state.visitorInfo.status !== Status.Ready) {
        throw new Error('[ gettyUpdateVisitorInfoSiteContentRequest ] Cannot update entity when `visitorInfo` is not in Ready state')
      }
      return {
        ...state,
        visitorInfo: {
          status: Status.Updating,
          data: state.visitorInfo.data,
        },
      }
    }

    case 'gettyUpdateVisitorInfoSiteContentSuccess': {
      if (state.visitorInfo.status !== Status.Updating) {
        throw new Error('[ gettyUpdateVisitorInfoSiteContentSuccess ] Cannot update entity when `visitorInfo` is not in Updating state')
      }
      return {
        ...state,
        visitorInfo: {
          status: Status.Stale,
          data: state.visitorInfo.data,
        }
      }
    }

    case 'gettyUpdateVisitorInfoSiteContentFailure': {
      return {
        ...state,
        visitorInfo: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    // }}} visitor info management.

    // {{{ integration.
    case 'gettyListTMSObjectsRequest': {
      const { page } = action.payload

      // if we are paginating, set to updating so we can update the resultset.
      if (state.tmsObjectList.status === Status.Ready) {
        if (!page || page && page.pageToken !== '') {
          return {
            ...state,
            tmsObjectList: {
              data: state.tmsObjectList.data,
              status:  Status.Updating,
            },
          }
        }
      }

      // otherwise this is a new resultset.
      return {
        ...state,
        tmsObjectList: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyListTMSObjectsSuccess': {
      const { nextPage, tmsObjects, totalItems } = action.payload

      const data: PaginatedList<TMSObject.Entity> = {
        entityList: state.tmsObjectList.status === Status.Updating
          ? [ ...state.tmsObjectList.data.entityList, ...tmsObjects ]
          : tmsObjects,
        nextPage: nextPage || undefined,
        totalItems,
      }

      return {
        ...state,
        tmsObjectList: {
          data,
          errors: [],
          status: Status.Ready,
        },
      }
    }
    case 'gettyListTMSObjectsFailure': {
      return {
        ...state,
        tmsObjectList: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyListGetty360EventsRequest': {
      return {
        ...state,
        events: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyListGetty360EventsSuccess': {
      if (!action.payload.getty360Events) {
        throw new Invariant('[ gettyListGetty360EventsSuccess ] Missing `getty360Events` in response payload')
      }
      const { getty360Events } = action.payload
      return {
        ...state,
        events: {
          status: Status.Ready,
          data: { getty360Events },
        },
      }
    }
    case 'gettyListGetty360EventsFailure': {
      return {
        ...state,
        events: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    // }}} integration.

    // {{{ structure access.
    case 'gettyListSitesRequest': {
      return {
        ...state,
        sites: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyListSitesSuccess': {
      if (!action.payload.sites) {
        throw new Invariant('[ gettyListSitesSuccess ] Missing `sites` in response payload')
      }
      const { sites } = action.payload
      return {
        ...state,
        sites: {
          status: Status.Ready,
          data: { sites },
        },
      }
    }
    case 'gettyListSitesFailure': {
      return {
        ...state,
        sites: {
          status: Status.Failed,
          messages: [action.message],
        }
      }
    }
    // }}} structure access.

    // {{{ analytics.
    case 'gettyGetAnalyticsRequest': {
      return {
        ...state,
        analytics: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyGetAnalyticsSuccess': {
      return {
        ...state,
        analytics: {
          status: Status.Ready,
          data: action.payload,
        },
      }
    }
    case 'gettyGetAnalyticsFailure': {
      return {
        ...state,
        sites: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    // }}} analytics.

    case 'gettyGetStopPointOfInterestRequest': {
      return {
        ...state,
        stopPointOfInterestUpdate: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyGetStopPointOfInterestSuccess': {
      const { resolvedValues, stopPointOfInterest, baseEntity, moods, tours } = action

      if (!stopPointOfInterest) {
        throw new Invariant('[ gettyGetStopPointOfInterestSuccess ] Missing `stopPointOfInterest` in response payload')
      }

      if (!stopPointOfInterest.stop) {
        throw new Invariant('[ gettyGetStopPointOfInterestSuccess ] Missing `stop` in response payload')
      }

      return {
        ...state,
        stopPointOfInterestUpdate: { status: Status.Ready, data: { stopPointOfInterest, resolvedValues, baseEntity, moods, tours } }
      }
    }
    case 'gettyGetStopPointOfInterestFailure': {
      return {
        ...state,
        stopPointOfInterestUpdate: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyCreateStopPointOfInterestRequest': {
      return {
        ...state,
        stopPointOfInterestCreate: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyCreateStopPointOfInterestSuccess': {
      if (!action.payload.stopPointOfInterest) {
        throw new Invariant('[ gettyCreateStopPointOfInterestSuccess ] Missing `stop point of interest` in response payload')
      }

      return {
        ...state,
        stopPointOfInterestCreate: {
          status: Status.Ready,
          data: action.payload.stopPointOfInterest,
        }
      }
    }
    case 'gettyCreateStopPointOfInterestFailure': {
      return {
        ...state,
        stopPointOfInterestCreate: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    case 'gettyCreateStopPointOfInterestReset': {
      return {
        ...state,
        stopPointOfInterestCreate: {
          status: Status.Idle,
        }
      }
    }
    case 'gettyUpdateStopPointOfInterestRequest': {
      if (state.stopPointOfInterestUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateStopPointOfInterestRequest ] Cannot update entity when `stopPointOfInterestUpdate` is not in Ready state')
      }

      return {
        ...state,
        stopPointOfInterestUpdate: {
          ...state.stopPointOfInterestUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateStopPointOfInterestSuccess': {
      if (!action.stopPointOfInterest) {
        throw new Invariant('[ gettyUpdateStopPointOfInterestSuccess ] Missing `stopPointOfInterest` in response payload')
      }
      const { stopPointOfInterest, resolvedValues, baseEntity } = action
      return {
        ...state,
        stopPointOfInterestUpdate: {
          status: Status.Ready,
          data: { stopPointOfInterest, resolvedValues, baseEntity},
        }
      }
    }
    case 'gettyUpdateStopPointOfInterestFailure': {
      return {
        ...state,
        stopPointOfInterestUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyUpdateStopPointOfInterestReset': {
      return {
        ...state,
        stopPointOfInterestUpdate: state.stopPointOfInterestUpdate.status === Status.Stale ? {
          data: state.stopPointOfInterestUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }
    case 'gettyDeleteStopPointOfInterestRequest': {
      return {
        ...state,
        stopPointOfInterestDelete: {
          ...state.stopPointOfInterestDelete,
          status: Status.Loading,
          ref: StopPointOfInterest.mustRef(action.payload.stopPointOfInterestRef)
        }
      }
    }
    case 'gettyDeleteStopPointOfInterestSuccess': {
      return {
        ...state,
        stopPointOfInterestDelete: {
          message: 'Stop deleted',
          status: Status.Complete,
          ref: StopPointOfInterest.mustRef(action.payload.ref)
        }
      }
    }
    case 'gettyDeleteStopPointOfInterestFailure': {
      return {
        ...state,
        stopPointOfInterestDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.stopPointOfInterestRef
        }
      }
    }
    case 'gettyDeleteStopPointOfInterestReset': {
      return {
        ...state,
        stopPointOfInterestDelete: {
          status: Status.Idle
        }
      }
    }

    case 'gettyGetEventPointOfInterestRequest': {
      return {
        ...state,
        eventPointOfInterestUpdate: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyGetEventPointOfInterestSuccess': {
      const { resolvedValues, eventPointOfInterest } = action

      if (!eventPointOfInterest) {
        throw new Invariant('[ gettyGetEventPointOfInterestSuccess ] Missing `eventPointOfInterest` in response payload')
      }

      if (!eventPointOfInterest.event) {
        throw new Invariant('[ gettyGetEventPointOfInterestSuccess ] Missing `event` in response payload')
      }

      return {
        ...state,
        eventPointOfInterestUpdate: { status: Status.Ready, data: { eventPointOfInterest, resolvedValues } }
      }
    }
    case 'gettyGetEventPointOfInterestFailure': {
      return {
        ...state,
        eventPointOfInterestUpdate: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyCreateEventPointOfInterestRequest': {
      return {
        ...state,
        eventPointOfInterestCreate: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyCreateEventPointOfInterestSuccess': {
      if (!action.payload.eventPointOfInterest) {
        throw new Invariant('[ gettyCreateEventPointOfInterestSuccess ] Missing `event point of interest` in response payload')
      }

      return {
        ...state,
        eventPointOfInterestCreate: {
          status: Status.Ready,
          data: action.payload.eventPointOfInterest,
        }
      }
    }
    case 'gettyCreateEventPointOfInterestFailure': {
      return {
        ...state,
        eventPointOfInterestCreate: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    case 'gettyCreateEventPointOfInterestReset': {
      return {
        ...state,
        eventPointOfInterestCreate: {
          status: Status.Idle,
        }
      }
    }
    case 'gettyUpdateEventPointOfInterestRequest': {
      if (state.eventPointOfInterestUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateEventPointOfInterestRequest ] Cannot update entity when `eventPointOfInterestUpdate` is not in Ready state')
      }

      return {
        ...state,
        eventPointOfInterestUpdate: {
          ...state.eventPointOfInterestUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateEventPointOfInterestSuccess': {
      if (!action.payload.eventPointOfInterest) {
        throw new Invariant('[ gettyUpdateEventPointOfInterestSuccess ] Missing `eventPointOfInterest` in response payload')
      }
      const { eventPointOfInterest } = action.payload
      return {
        ...state,
        eventPointOfInterestUpdate: {
          status: Status.Ready,
          data: { eventPointOfInterest },
        }
      }
    }
    case 'gettyUpdateEventPointOfInterestFailure': {
      return {
        ...state,
        eventPointOfInterestUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyUpdateEventPointOfInterestReset': {
      return {
        ...state,
        eventPointOfInterestUpdate: state.eventPointOfInterestUpdate.status === Status.Stale ? {
          data: state.eventPointOfInterestUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }
    case 'gettyDeleteEventPointOfInterestRequest': {
      return {
        ...state,
        eventPointOfInterestDelete: {
          ...state.eventPointOfInterestDelete,
          status: Status.Loading,
          ref: EventPointOfInterest.mustRef(action.payload.eventPointOfInterestRef)
        }
      }
    }
    case 'gettyDeleteEventPointOfInterestSuccess': {
      return {
        ...state,
        eventPointOfInterestDelete: {
          message: 'Event deleted',
          status: Status.Complete,
          ref: EventPointOfInterest.mustRef(action.payload.ref)
        }
      }
    }
    case 'gettyDeleteEventPointOfInterestFailure': {
      return {
        ...state,
        eventPointOfInterestDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.eventPointOfInterestRef
        }
      }
    }
    case 'gettyDeleteEventPointOfInterestReset': {
      return {
        ...state,
        eventPointOfInterestDelete: {
          status: Status.Idle
        }
      }
    }

    case 'gettyGetGenericPointOfInterestRequest': {
      return {
        ...state,
        genericPointOfInterestUpdate: {
          status:  Status.Loading,
        },
      }
    }
    case 'gettyGetGenericPointOfInterestSuccess': {
      const { genericPointOfInterest } = action

      if (!genericPointOfInterest) {
        throw new Invariant('[ gettyGetGenericPointOfInterestSuccess ] Missing `genericPointOfInterest` in response payload')
      }

      return {
        ...state,
        genericPointOfInterestUpdate: { status: Status.Ready, data: { genericPointOfInterest } }
      }
    }
    case 'gettyGetGenericPointOfInterestFailure': {
      return {
        ...state,
        genericPointOfInterestUpdate: {
          messages: [action.message],
          status: Status.Failed,
        },
      }
    }
    case 'gettyCreateGenericPointOfInterestRequest': {
      return {
        ...state,
        genericPointOfInterestCreate: {
          status: Status.Loading,
        },
      }
    }
    case 'gettyCreateGenericPointOfInterestSuccess': {
      if (!action.payload.genericPointOfInterest) {
        throw new Invariant('[ gettyCreateGenericPointOfInterestSuccess ] Missing `generic point of interest` in response payload')
      }

      return {
        ...state,
        genericPointOfInterestCreate: {
          status: Status.Ready,
          data: action.payload.genericPointOfInterest,
        }
      }
    }
    case 'gettyCreateGenericPointOfInterestFailure': {
      return {
        ...state,
        genericPointOfInterestCreate: {
          status: Status.Failed,
          messages: [ action.message ],
        }
      }
    }
    case 'gettyCreateGenericPointOfInterestReset': {
      return {
        ...state,
        genericPointOfInterestCreate: {
          status: Status.Idle,
        }
      }
    }
    case 'gettyUpdateGenericPointOfInterestRequest': {
      if (state.genericPointOfInterestUpdate.status !== Status.Ready) {
        throw new Error('[ gettyUpdateGenericPointOfInterestRequest ] Cannot update entity when `genericPointOfInterestUpdate` is not in Ready state')
      }

      return {
        ...state,
        genericPointOfInterestUpdate: {
          ...state.genericPointOfInterestUpdate,
          status: Status.Updating
        }
      }
    }
    case 'gettyUpdateGenericPointOfInterestSuccess': {
      if (!action.genericPointOfInterest) {
        throw new Invariant('[ gettyUpdateGenericPointOfInterestSuccess ] Missing `genericPointOfInterest` in response payload')
      }
      const { genericPointOfInterest } = action
      return {
        ...state,
        genericPointOfInterestUpdate: {
          status: Status.Ready,
          data: { genericPointOfInterest },
        }
      }
    }
    case 'gettyUpdateGenericPointOfInterestFailure': {
      return {
        ...state,
        genericPointOfInterestUpdate: { status: Status.Failed, messages: [action.message]  }
      }
    }
    case 'gettyUpdateGenericPointOfInterestReset': {
      return {
        ...state,
        genericPointOfInterestUpdate: state.genericPointOfInterestUpdate.status === Status.Stale ? {
          data: state.genericPointOfInterestUpdate.data,
          status:  Status.Ready,
        } : {
          status: Status.Idle,
        }
      }
    }
    case 'gettyDeleteGenericPointOfInterestRequest': {
      return {
        ...state,
        genericPointOfInterestDelete: {
          ...state.genericPointOfInterestDelete,
          status: Status.Loading,
          ref: GenericPointOfInterest.mustRef(action.payload.genericPointOfInterestRef)
        }
      }
    }
    case 'gettyDeleteGenericPointOfInterestSuccess': {
      return {
        ...state,
        genericPointOfInterestDelete: {
          message: 'Generic deleted',
          status: Status.Complete,
          ref: GenericPointOfInterest.mustRef(action.payload.ref)
        }
      }
    }
    case 'gettyDeleteGenericPointOfInterestFailure': {
      return {
        ...state,
        genericPointOfInterestDelete: {
          status: Status.Failed,
          messages: [action.message],
          ref: action.genericPointOfInterestRef
        }
      }
    }
    case 'gettyDeleteGenericPointOfInterestReset': {
      return {
        ...state,
        genericPointOfInterestDelete: {
          status: Status.Idle
        }
      }
    }

    default: {
      assertNever(action)
      throw new Invariant()
    }
  }
}

function* gettySagas() {
  // @ts-ignore
  return yield all([ ...gettyRootSaga ])
}

type GettyModuleState = {
  readonly getty: GettyAppState;
}

export const getGettyModule = (): ISagaModule<GettyModuleState> => ({
  id: 'getty',
  reducerMap: {
    getty: gettyReducer,
  } as any,
  retained: true,
  sagas: [ gettySagas ],
})
